import React, { useRef } from 'react';

import { Button, Card, Flex, Icon, InputBase, Text } from '@opendoor/bricks/core';

import { useMobileDevice } from 'shared/helpers/hooks/useMobileDevice';

import { FileUpload } from 'sellDirect/helpers/FileUploadClient';

interface IPreApprovalConfirmationProps {
  preApprovalDoc: FileUpload;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onForward: () => void;
  onBack: () => void;
  isUploading: boolean;
}

/**
 * PreApprovalConfirmation step
 * storybook-check-ignore
 */
export const PreApprovalConfirmation: React.FC<IPreApprovalConfirmationProps> = ({
  preApprovalDoc,
  onFileChange,
  onForward,
  onBack,
  isUploading,
}) => {
  const { isMobile } = useMobileDevice();
  const hiddenInputRef = useRef<HTMLInputElement | null>(null);

  // The hidden input is used to trigger the file upload modal and needs to be "clicked" programmatically
  const triggerUploadModal = () => hiddenInputRef?.current?.click();

  return (
    <Flex flexDirection="column" justifyContent="flex-start" width={['100%', null, '100%']} mb={8}>
      <Flex
        flexDirection="column"
        alignItems="flex-start"
        width={['100%', null, '100%']}
        justifyContent="space-between"
        mt={8}
      >
        <Text
          size={isMobile ? '160' : '150'}
          fontWeight="medium"
          mx={[0, null, 0]}
          alignSelf="flex-start"
        >
          We got your pre-approval letter. Now, let’s review everything.
        </Text>
        <Card variant="outline" mt={8} width={['100%', null, '100%']}>
          <Flex flexDirection="row" alignItems="center">
            <Icon size={24} name="offer-inactive" color="neutrals100" aria-hidden="true" />
            <Text size="110" fontWeight="medium" ml={4} mr={6}>
              {preApprovalDoc?.file?.name}
            </Text>
            <Icon size={24} name="checkmark" color="green50" aria-hidden="true" />
          </Flex>
        </Card>
        <InputBase
          ref={hiddenInputRef}
          type="file"
          // https://www.a11yproject.com/posts/2013-01-11-how-to-hide-content/
          border={0}
          height="1px"
          overflow="hidden"
          padding={0}
          position="absolute"
          whiteSpace="nowrap"
          width="1px"
          accept="application/pdf,image/png,image/jpeg,image/jpg"
          onChange={onFileChange}
        />
        <Button
          analyticsName="buy-direct-upload-different-file"
          size="lg"
          variant="ghost"
          onClick={triggerUploadModal}
          aria-label="Upload a different file"
          mt={6}
        >
          <Text size="110" fontWeight="medium" color="brand50">
            Upload a different file
          </Text>
        </Button>
        <Flex
          flexDirection={['column', null, 'row-reverse']}
          alignItems="center"
          justifyContent="end"
          mt={8}
          width="100%"
        >
          <Button
            analyticsName="buy-direct-checkout-review-details"
            aria-label="Review details for contract"
            data-testid="buy-direct-checkout-review-details-button"
            loading={isUploading}
            mt={8}
            onClick={onForward}
            variant="primary"
            width={['100%', '100%', '55%', '40%']}
            ml={[null, null, 4]}
          >
            Review details for contract
          </Button>
          <Button
            analyticsName="buy-direct-checkout-pre-approval-confirmation-back"
            aria-label="Back"
            data-testid="buy-direct-checkout-pre-approval-confirmation-button"
            width={['100%', null, '25%']}
            variant="secondary"
            onClick={onBack}
            mt={8}
            mr={[null, null, 4]}
          >
            Back
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
